'use client';

import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  IntlFieldDef,
  IntlStepKey,
  START_AVAILABILITY_LABELS,
  STEP_FIELDS,
  intlFieldShown,
} from '@/lib/recruitment/intl-application';
import type { FormValues } from '@/lib/recruitment/intl-form-engine';
import {
  CheckboxGroup,
  DurationInput,
  ErrorHint,
  FieldLabel,
  NumberInput,
  RadioGroup,
  TextInput,
  TextareaInput,
  TickBox,
  YesNo,
} from '@/components/recruitment/intl/fields';

export type IntlFormValue = string | boolean | string[] | null;
export type IntlFormState = Record<string, IntlFormValue>;

export type SaveStepResult = { ok: boolean; missing?: string[]; error?: string };

/** Browser input types for free-text string columns (better mobile keyboards). */
const INPUT_TYPES: Record<string, string> = {
  email: 'email',
  mobile_number: 'tel',
  whatsapp_number: 'tel',
  facebook_url: 'url',
  linkedin_url: 'url',
};

function isOtherSelected(def: IntlFieldDef, value: IntlFormValue): boolean {
  return Array.isArray(value) ? value.includes('Other') : value === 'Other';
}

/** Asked right now, given the other answers (dependsOn parent and showWhen condition). */
function isShown(def: IntlFieldDef, form: IntlFormState): boolean {
  return intlFieldShown(def, form as FormValues);
}

type MissingField = { column: string; label: string };

/**
 * Client-side mirror of the step-save route's required/dependsOn/showWhen
 * rules - returns the missing or invalid fields for the error summary (by
 * column: two blocks may ask the same question, e.g. the backup device specs).
 */
function getMissingFields(defs: IntlFieldDef[], form: IntlFormState): MissingField[] {
  const otherColumns = new Set(defs.filter((d) => d.otherColumn).map((d) => d.otherColumn!));
  const missing: MissingField[] = [];
  for (const def of defs) {
    if (otherColumns.has(def.column)) continue; // '*_other' companions are optional
    if (!isShown(def, form)) continue; // hidden - nulled in the payload
    let required = def.required === true;
    if (def.dependsOn) required = !def.optional;

    const raw = form[def.column];
    if ((def.type === 'number' || def.type === 'duration') && typeof raw === 'string' && raw.trim()) {
      const n = Number(raw);
      const invalid =
        !Number.isFinite(n) ||
        (def.min !== undefined && n < def.min) ||
        (def.max !== undefined && n > def.max) ||
        (def.type === 'duration' && n < 0);
      if (invalid) {
        missing.push({ column: def.column, label: def.label });
        continue;
      }
    }
    if (!required) continue;

    const empty =
      raw === null ||
      raw === undefined ||
      (typeof raw === 'string' && !raw.trim()) ||
      (Array.isArray(raw) && raw.length === 0);
    if (empty) missing.push({ column: def.column, label: def.label });
  }
  return missing;
}

/** Build the PUT payload the coercion on the server expects (typed values). */
function buildPayload(defs: IntlFieldDef[], form: IntlFormState): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const def of defs) {
    // The server coerces values before applying the conditions - a stale
    // value in a hidden field must not reach it.
    if (!isShown(def, form)) {
      payload[def.column] = null;
      continue;
    }
    const raw = form[def.column];
    switch (def.type) {
      case 'bool':
        payload[def.column] = typeof raw === 'boolean' ? raw : null;
        break;
      case 'number':
      case 'duration': {
        const n = typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;
        payload[def.column] = Number.isFinite(n) ? n : null;
        break;
      }
      case 'string_array':
        payload[def.column] = Array.isArray(raw) ? raw : [];
        break;
      default:
        payload[def.column] = typeof raw === 'string' ? raw : null;
    }
  }
  return payload;
}

function FieldControl({
  def,
  form,
  setField,
  hasError,
}: {
  def: IntlFieldDef;
  form: IntlFormState;
  setField: (column: string, value: IntlFormValue) => void;
  hasError: boolean;
}) {
  const raw = form[def.column];
  switch (def.type) {
    case 'bool':
      return (
        <YesNo
          value={typeof raw === 'boolean' ? raw : null}
          onChange={(v) => setField(def.column, v)}
          hasError={hasError}
        />
      );
    case 'string_array': {
      const values = Array.isArray(raw) ? raw : [];
      return (
        <CheckboxGroup
          values={values}
          onChange={(next) => {
            // An exclusive option ("I do not have...") cannot be combined:
            // picking it clears the rest, picking anything else drops it.
            const exclusive = def.exclusiveOption;
            if (exclusive && next.includes(exclusive) && next.length > 1) {
              setField(def.column, values.includes(exclusive) ? next.filter((v) => v !== exclusive) : [exclusive]);
              return;
            }
            setField(def.column, next);
          }}
          options={def.options ?? []}
          hasError={hasError}
        />
      );
    }
    case 'number':
      return (
        <NumberInput
          value={typeof raw === 'string' ? raw : ''}
          onChange={(v) => setField(def.column, v)}
          min={def.min}
          max={def.max}
          readOnly={def.column === 'age'}
          placeholder={def.column === 'age' ? 'Calculated from your date of birth' : def.placeholder}
          hasError={hasError}
        />
      );
    case 'duration':
      return (
        <DurationInput
          value={typeof raw === 'string' ? raw : ''}
          onChange={(v) => setField(def.column, v)}
          hasError={hasError}
        />
      );
    case 'date':
      return (
        <TextInput
          type="date"
          value={typeof raw === 'string' ? raw : ''}
          onChange={(v) => setField(def.column, v)}
          hasError={hasError}
        />
      );
    case 'text':
      return (
        <TextareaInput
          value={typeof raw === 'string' ? raw : ''}
          onChange={(v) => setField(def.column, v)}
          rows={3}
          placeholder={def.placeholder}
          hasError={hasError}
        />
      );
    default: {
      // 'string'
      if (def.options) {
        return (
          <RadioGroup
            value={typeof raw === 'string' ? raw : ''}
            onChange={(v) => setField(def.column, v)}
            options={def.options}
            labels={def.optionLabels ?? (def.column === 'start_availability' ? START_AVAILABILITY_LABELS : undefined)}
            hasError={hasError}
          />
        );
      }
      const text = typeof raw === 'string' ? raw : '';
      const unknown = !!def.unknownOption && text === def.unknownOption;
      return (
        <>
          <TextInput
            type={INPUT_TYPES[def.column] ?? 'text'}
            value={text}
            onChange={(v) => setField(def.column, v)}
            placeholder={def.placeholder}
            readOnly={unknown}
            hasError={hasError}
          />
          {def.unknownOption && (
            <TickBox
              checked={unknown}
              label={def.unknownOption}
              onChange={(checked) => setField(def.column, checked ? def.unknownOption! : '')}
            />
          )}
        </>
      );
    }
  }
}

export function DataStep({
  step,
  defs: defsOverride,
  form,
  setField,
  onSave,
  onBack,
  extraContent,
  slotsAfter,
}: {
  step: IntlStepKey;
  /** The step's questions as worded in Recruitment -> Job Applications (code defaults otherwise). */
  defs?: IntlFieldDef[];
  form: IntlFormState;
  setField: (column: string, value: IntlFormValue) => void;
  /** Persist this step (the wizard owns the PUT + advance). */
  onSave: (payload: Record<string, unknown>) => Promise<SaveStepResult>;
  onBack: (() => void) | null;
  /** Extra block rendered after the fields (e.g. an upload card). */
  extraContent?: ReactNode;
  /** Blocks rendered right after a given question (by column), only while that question is shown. */
  slotsAfter?: Record<string, ReactNode>;
}) {
  const defs = defsOverride ?? STEP_FIELDS[step];
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [serverMissing, setServerMissing] = useState<string[]>([]);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  const otherColumns = useMemo(
    () => new Set(defs.filter((d) => d.otherColumn).map((d) => d.otherColumn!)),
    [defs]
  );
  const missing = useMemo(() => getMissingFields(defs, form), [defs, form]);

  const isFieldMissing = (def: IntlFieldDef) =>
    showErrors && missing.some((m) => m.column === def.column);

  const handleSave = async () => {
    setServerError('');
    setServerMissing([]);
    if (missing.length > 0) {
      setShowErrors(true);
      setTimeout(
        () => errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        50
      );
      return;
    }
    setSaving(true);
    try {
      const result = await onSave(buildPayload(defs, form));
      if (!result.ok) {
        if (result.missing?.length) setServerMissing(result.missing);
        if (result.error) setServerError(result.error);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {showErrors && missing.length > 0 && (
        <div ref={errorSummaryRef} className="bg-red-900/30 border border-red-600 rounded-2xl p-5">
          <p className="text-sm font-semibold text-red-300 mb-3 flex items-center gap-2">
            <span>⚠</span> Please complete the following before continuing:
          </p>
          <ul className="space-y-1">
            {missing.map((m) => (
              <li key={m.column} className="text-xs text-red-400 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                {m.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {defs.map((def) => {
        // '*_other' companions render inside their parent's reveal, not standalone.
        if (otherColumns.has(def.column)) return null;
        // Questions hidden by an earlier answer are not rendered at all.
        if (def.showWhen && !isShown(def, form)) return null;

        const otherDef = def.otherColumn
          ? defs.find((d) => d.column === def.otherColumn)
          : undefined;
        const otherOpen = def.otherColumn ? isOtherSelected(def, form[def.column]) : false;
        const dependsOpen = def.dependsOn ? form[def.dependsOn] === true : true;
        const slot = slotsAfter?.[def.column];

        const body = (
          <div>
            {def.heading && (
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-400 pt-2 mb-3 border-t border-gray-800">
                {def.heading}
              </p>
            )}
            <FieldLabel label={def.label} required={def.required || (!!def.dependsOn && !def.optional)} />
            {def.help && <p className="text-xs text-gray-500 -mt-1 mb-2">{def.help}</p>}
            <FieldControl def={def} form={form} setField={setField} hasError={isFieldMissing(def)} />
            <ErrorHint show={isFieldMissing(def)} />
            {otherDef && (
              <div
                className={`transition-all overflow-hidden ${
                  otherOpen ? 'max-h-40 opacity-100 mt-2' : 'max-h-0 opacity-0 pointer-events-none'
                }`}
              >
                <FieldLabel label={otherDef.label} />
                <TextareaInput
                  value={typeof form[otherDef.column] === 'string' ? (form[otherDef.column] as string) : ''}
                  onChange={(v) => setField(otherDef.column, v)}
                  rows={2}
                  placeholder="Please specify…"
                />
              </div>
            )}
            {slot && <div className="mt-5">{slot}</div>}
          </div>
        );

        if (def.dependsOn) {
          return (
            <div
              key={def.column}
              className={`transition-all overflow-hidden ${
                dependsOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0 pointer-events-none -mt-5'
              }`}
            >
              {body}
            </div>
          );
        }
        return <div key={def.column}>{body}</div>;
      })}

      {extraContent}

      {(serverError || serverMissing.length > 0) && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 rounded-xl px-4 py-3 text-sm">
          <p>{serverError || 'Some fields are missing or invalid:'}</p>
          {serverMissing.length > 0 && (
            <ul className="mt-2 space-y-1">
              {serverMissing.map((label, i) => (
                <li key={`${i}-${label}`} className="text-xs flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                  {label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-5 py-3.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm font-medium transition-colors"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="btn btn-primary flex-1 py-3.5 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 h-auto"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving…
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
}
